import type { LayerConfigDocument, LayerNodeEntry } from '~/api/layerConfig';

// ---------------------------------------------------------------------------
// Pure document ↔ store-state mapping for the per-user layer config.
//
// Translates the backend document to/from the
// slice of runtime state the layer store cares about, and back. No I/O, no valtio —
// the store (store2/layerConfig.ts) adapts these plain Set/Map values into its
// proxies, and the dirty-diff / debounced sync reads the same seam.
//
// The document is **leaf-only and state-minimal**: one `{ visible, pinned?,
// missingSince? }` entry per leaf id; sections/groups are derived from the catalog at
// render time and never stored. Expansion is device-local localStorage state
// (`Prefs.EXPANDED_NODE_IDS`) and order follows catalog position — neither is ever
// serialized. The round-trip is **non-lossy by construction**: `stateToDocument`
// re-emits every id the hydrated document had (plus Library-membership edits) with
// its `visible`/`pinned` from the editable sets and its server-written `missingSince`
// stamp untouched — ids the catalog can't place are carried through, never dropped,
// so a transient catalog gap can never turn a sync into data loss.
// ---------------------------------------------------------------------------

/**
 * The layer-config slice of store state. `nodeIds` is the complete, order-preserving
 * document key set (leaf ids — including ids the current catalog can't place, which
 * must survive the round-trip untouched); `visibleLeafIds` / `pinnedLeafIds` are the
 * editable sets; `missingSince` carries the server's GC stamps verbatim (the client
 * never interprets them).
 */
export interface LayerConfigState {
  /** Every document leaf id, in document order — the membership the write-back re-emits. */
  nodeIds: string[];
  /** Ids whose picker checkbox is on. */
  visibleLeafIds: Set<string>;
  /** Ids bookmarked (the picker pin). */
  pinnedLeafIds: Set<string>;
  /** id → server-written GC stamp, echoed back untouched. */
  missingSince: Map<string, string>;
}

/**
 * Project a hydrated layer-config document into the store-state slice. Every entry —
 * catalog-known or not — lands in the sets, so a later {@link stateToDocument}
 * reproduces the document exactly (the render mirror, not this mapping, is where
 * catalog-unknown ids are filtered from the visible map).
 */
export function documentToState(document: LayerConfigDocument): LayerConfigState {
  const nodeIds: string[] = [];
  const visibleLeafIds = new Set<string>();
  const pinnedLeafIds = new Set<string>();
  const missingSince = new Map<string, string>();

  for (const [nodeId, entry] of Object.entries(document.nodes)) {
    nodeIds.push(nodeId);
    if (entry.visible) visibleLeafIds.add(nodeId);
    if (entry.pinned) pinnedLeafIds.add(nodeId);
    if (entry.missingSince !== undefined) missingSince.set(nodeId, entry.missingSince);
  }

  return { nodeIds, visibleLeafIds, pinnedLeafIds, missingSince };
}

/**
 * Rebuild the whole document from store state: the hydrated document's id set (plus
 * membership edits), each entry's `visible`/`pinned` from the editable sets, and the
 * server's `missingSince` stamp echoed back untouched. `pinned` is emitted sparsely
 * (present only when true, mirroring the server's omit-when-absent shape). Never a
 * reconstruction of the rendered tree — an id the catalog can't place serializes
 * exactly like any other, so a transient catalog gap can't subtract from the record.
 * Inverse of {@link documentToState}. This is what the sync's `PUT` serializes; it
 * performs no validation — the server is the authority.
 */
export function stateToDocument(state: LayerConfigState): LayerConfigDocument {
  const nodes: Record<string, LayerNodeEntry> = {};
  for (const nodeId of state.nodeIds) {
    const stamp = state.missingSince.get(nodeId);
    nodes[nodeId] = {
      visible: state.visibleLeafIds.has(nodeId),
      ...(state.pinnedLeafIds.has(nodeId) ? { pinned: true } : {}),
      ...(stamp !== undefined ? { missingSince: stamp } : {}),
    };
  }
  return { nodes };
}
